import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { TreeView } from './components/TreeView';
import type { HierarchyFilter } from './components/TreeView';
import { MachineDetail } from './components/MachineDetail';
import { DashboardSummary } from './components/DashboardSummary';
import { Reports } from './components/Reports';
import { AlarmManager } from './components/AlarmManager';
import { AnomalyIndicatorsPage } from './components/AnomalyIndicatorsPage';
import { BearingHealth } from './components/BearingHealth';
import { VelocityTriage } from './components/VelocityTriage';
import { UserManagement } from './components/UserManagement';
import { DatabaseManagement } from './components/DatabaseManagement';
import { AddMachine } from './components/AddMachine';
import { ReviewDevices } from './components/ReviewDevices';
import { AddDevice } from './components/AddDevice';
import { AlarmReportProvider } from './contexts/AlarmReportContext';
import { useState, useRef, useEffect } from 'react';
import { getToken, getUser, clearAuth, apiLogin, ApiError, UNAUTHORIZED_EVENT } from './api/client';

type FormSubmitEvent = { preventDefault(): void };

interface Point {
  id: string;
  name: string;
  velocity: { V: number; H: number; A: number };
  acceleration: { V: number; H: number; A: number };
  envelope: { V: number; H: number; A: number };
  temperature: { V: number; H: number; A: number };
  status: 'low' | 'medium' | 'high';
}

// ---------------------------------------------------------------------------
// Login page
// ---------------------------------------------------------------------------
function LoginPage({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormSubmitEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiLogin(email, password);
      onLogin();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1117] via-[#111820] to-[#0d1117] flex items-center justify-center">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Logo */}
        <img
          src="/logo.svg"
          alt="PerfectCircle"
          className="w-80 mb-8 select-none"
          draggable={false}
        />
        {/* Login card */}
        <div className="w-full bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 shadow-2xl p-10">
          <p className="text-center text-[#00D4BE]/70 text-sm tracking-widest uppercase mb-8">Vibration Monitoring Platform</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#00D4BE]/50 transition-colors"
                placeholder="admin@pc-reliability.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#00D4BE]/50 transition-colors"
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#00D4BE] text-[#0d1117] font-semibold rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main app
// ---------------------------------------------------------------------------
export default function App() {
  const [authed, setAuthed] = useState(() => {
    const token = getToken();
    const user = getUser();
    if (token && !user) { clearAuth(); return false; }
    return !!token;
  });
  const [activeView, setActiveView] = useState<string>(() =>
    sessionStorage.getItem('pc_view') || 'dashboard'
  );
  const [selectedMachine, setSelectedMachine] = useState<{
    id: string;
    name: string;
    points: Point[];
    initialPointId?: string;
    initialPointName?: string;
    initialAxisNum?: number;
    pointNonce?: number;   // bumps on every sidebar point/axis click so the same point re-opens
    waveformPointFilter?: string;
    imageUrl?: string;
  } | null>(() => {
    try {
      const saved = sessionStorage.getItem('pc_machine');
      if (!saved) return null;
      const machine = JSON.parse(saved);
      // Restore the selected point detail page if the user was on one when they reloaded
      try {
        const savedPoint = sessionStorage.getItem('pc_point');
        if (savedPoint) {
          const { machineId, pointName } = JSON.parse(savedPoint);
          if (machineId === machine.id && pointName) machine.initialPointName = pointName;
        }
      } catch {}
      return machine;
    } catch { return null; }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [prefillAlarm, setPrefillAlarm] = useState<{ alarmId: string; machineName: string; metric: string } | null>(null);
  const [hierarchyFilter, setHierarchyFilter] = useState<HierarchyFilter | null>(null);
  const treeViewRef = useRef<any>(null);
  const pointClickSeq = useRef(0);

  // Re-check token on focus (handles expiry in other tabs); route to login when a
  // request 401s mid-session. apiFetch clears auth + fires UNAUTHORIZED_EVENT once
  // per expiry (no full-page reload), so this just flips to the login screen —
  // unmounting the data pages stops the failing fetches instead of looping.
  useEffect(() => {
    const check = () => setAuthed(!!getToken());
    const onUnauthorized = () => setAuthed(false);
    window.addEventListener('focus', check);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener('focus', check);
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, []);

  // Persist navigation state so refresh restores the current page
  useEffect(() => {
    sessionStorage.setItem('pc_view', activeView);
  }, [activeView]);

  useEffect(() => {
    if (selectedMachine) {
      sessionStorage.setItem('pc_machine', JSON.stringify({ id: selectedMachine.id, name: selectedMachine.name, points: [] }));
    } else {
      sessionStorage.removeItem('pc_machine');
    }
  }, [selectedMachine]);

  // Dynamic page title — must be before any early returns (Rules of Hooks)
  useEffect(() => {
    if (!authed) { document.title = 'Login — PerfectCircle'; return; }
    if (activeView === 'reports') { document.title = 'Reports — PerfectCircle'; return; }
    if (activeView === 'alarm-manager') { document.title = 'Alarm Manager — PerfectCircle'; return; }
    if (activeView === 'users') { document.title = 'Users — PerfectCircle'; return; }
    if (activeView === 'databases') { document.title = 'Databases — PerfectCircle'; return; }
    if (activeView === 'anomaly-indicators') { document.title = 'Anomaly Indicators — PerfectCircle'; return; }
    if (activeView === 'bearings') { document.title = 'Impact Score — PerfectCircle'; return; }
    if (activeView === 'velocity-triage') { document.title = 'Velocity Triage — PerfectCircle'; return; }
    if (activeView === 'machines') { document.title = 'Machines — PerfectCircle'; return; }
    if (activeView === 'machines-add') { document.title = 'Add Machine — PerfectCircle'; return; }
    if (activeView === 'machines-copy') { document.title = 'Copy Machine — PerfectCircle'; return; }
    if (activeView === 'devices-add') { document.title = 'Add Device — PerfectCircle'; return; }
    if (activeView === 'devices-review') { document.title = 'Review Devices — PerfectCircle'; return; }
    if (selectedMachine) { document.title = `${selectedMachine.name} — PerfectCircle`; return; }
    document.title = 'Dashboard — PerfectCircle';
  }, [authed, activeView, selectedMachine]);

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  const handleMachineClick = (machineId: string, machineName: string, points: Point[], imageUrl?: string) => {
    sessionStorage.removeItem('pc_point'); // clear any previous point selection from a different machine
    setSelectedMachine({ id: machineId, name: machineName, points, imageUrl });
  };

  const handlePointClick = (machineId: string, machineName: string, points: Point[], pointId: string, imageUrl?: string, axisNum?: number) => {
    // pointId is the point name. Route to the Point page: MachineDetail's
    // "Auto-open PointDetail when navigating from sidebar point/axis click" effect
    // opens PointDetail when initialPointId is set (initialAxisNum preselects the axis).
    // pointNonce bumps every click so re-clicking the same point (after Back) re-opens it.
    pointClickSeq.current += 1;
    setSelectedMachine({ id: machineId, name: machineName, points, initialPointId: pointId, initialAxisNum: axisNum, pointNonce: pointClickSeq.current, imageUrl });
  };

  const handleVisitTrend = (machineId: string, machineName: string, pointName: string) => {
    setSelectedMachine({ id: machineId, name: machineName, points: [], initialPointName: pointName });
  };

  const handleBackToTree = () => {
    setSelectedMachine(null);
    setSidebarCollapsed(false);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleImageUpload = (machineId: string, imageUrl: string) => {
    if (selectedMachine && selectedMachine.id === machineId) {
      setSelectedMachine({ ...selectedMachine, imageUrl });
    }
    if (treeViewRef.current) {
      treeViewRef.current.updateMachineImage(machineId, imageUrl);
    }
  };

  const handleViewReport = (reportId: string) => {
    setSelectedReportId(reportId);
    setActiveView('reports');
  };

  const handleCreateReportFromAlarm = (alarm: { alarmId: string; machineName: string; metric: string }) => {
    setPrefillAlarm(alarm);
    setActiveView('reports');
  };

  return (
    <AlarmReportProvider>
      <div className="flex h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
        <Sidebar
          activeView={activeView}
          treeViewContent={
            <TreeView
              onMachineSelect={handleMachineClick}
              onPointSelect={handlePointClick}
              onFilterSelect={setHierarchyFilter}
              activeFilter={hierarchyFilter}
              ref={treeViewRef}
            />
          }
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
        />
        <div className="flex-1 flex flex-col overflow-hidden relative z-0">
          <Header
            onNavigateDashboard={() => { setActiveView('dashboard'); setSelectedMachine(null); setSidebarCollapsed(false); }}
            activeView={activeView}
            onViewChange={(view) => { setActiveView(view); }}
            isAdmin={getUser()?.role === 'admin'}
          />
          <main className="flex-1 overflow-y-auto p-6 space-y-6">
            {activeView === 'dashboard' && (
              <>
                {selectedMachine ? (
                  <MachineDetail
                    machineId={selectedMachine.id}
                    machineName={selectedMachine.name}
                    points={selectedMachine.points}
                    initialPointId={selectedMachine.initialPointId}
                    initialPointName={selectedMachine.initialPointName}
                    initialAxisNum={selectedMachine.initialAxisNum}
                    pointNonce={selectedMachine.pointNonce}
                    waveformPointFilter={selectedMachine.waveformPointFilter}
                    imageUrl={selectedMachine.imageUrl}
                    onBack={handleBackToTree}
                    onImageUpload={handleImageUpload}
                  />
                ) : (
                  <DashboardSummary
                    machines={treeViewRef.current?.getAllMachines() || []}
                    onMachineClick={handleMachineClick}
                    onPointClick={handleVisitTrend}
                    onViewReport={handleViewReport}
                    onCreateReport={handleCreateReportFromAlarm}
                    isAdmin={getUser()?.role === 'admin'}
                    hierarchyFilter={hierarchyFilter}
                    onClearFilter={() => setHierarchyFilter(null)}
                  />
                )}
              </>
            )}
            {activeView === 'reports' && (
              <Reports
                selectedReportId={selectedReportId}
                prefillAlarm={prefillAlarm}
                onMachineClick={(machineId, machineName, points, imageUrl) => {
                  handleMachineClick(machineId, machineName, points as any, imageUrl);
                  setActiveView('dashboard');
                }}
              />
            )}
            {activeView === 'anomaly-indicators' && (
              <AnomalyIndicatorsPage
                onMachineClick={(machineId, machineName, points) => {
                  handleMachineClick(machineId, machineName, points);
                  setActiveView('dashboard');
                }}
              />
            )}
            {activeView === 'bearings' && (
              <BearingHealth
                onMachineClick={(machineId, machineName, points, _imageUrl, initialPointName) => {
                  sessionStorage.removeItem('pc_point');
                  setSelectedMachine({ id: machineId, name: machineName, points: [], initialPointName });
                  setActiveView('dashboard');
                }}
              />
            )}
            {activeView === 'velocity-triage' && (
              <VelocityTriage
                onMachineClick={(machineId, machineName) => {
                  setSelectedMachine({ id: machineId, name: machineName, points: [], initialPointName: undefined });
                  setActiveView('dashboard');
                }}
              />
            )}
            {activeView === 'alarm-manager' && getUser()?.role === 'admin' && (
              <AlarmManager />
            )}
            {activeView === 'users' && getUser()?.role === 'admin' && (
              <UserManagement />
            )}
            {activeView === 'databases' && getUser()?.role === 'admin' && (
              <DatabaseManagement />
            )}
            {activeView === 'machines' && getUser()?.role === 'admin' && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
                <span className="text-lg font-medium text-gray-500">Machines</span>
                <span className="text-sm">Machine management coming soon.</span>
              </div>
            )}
            {activeView === 'machines-add' && getUser()?.role === 'admin' && (
              <AddMachine onSaved={() => {}} />
            )}
            {activeView === 'machines-copy' && getUser()?.role === 'admin' && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
                <span className="text-lg font-medium text-gray-500">Copy Machine</span>
                <span className="text-sm">Copy machine form coming soon.</span>
              </div>
            )}
            {activeView === 'devices-add' && getUser()?.role === 'admin' && (
              <AddDevice />
            )}
            {activeView === 'devices-review' && getUser()?.role === 'admin' && (
              <ReviewDevices
                hierarchyFilter={hierarchyFilter}
                onClearFilter={() => setHierarchyFilter(null)}
              />
            )}
          </main>
        </div>
      </div>
    </AlarmReportProvider>
  );
}
